(() => {
  'use strict';

  // Ajax cart writes and Shopify actions share one queue. A lost response is
  // reconciled by reading the cart; it never causes another add or removal.
  let tail = null;
  const run = (operation) => {
    // Reserve the queue before dispatching a pending event: a synchronous app
    // listener can itself call updateCart during that dispatch.
    const task = (tail || Promise.resolve()).then(operation, operation);
    tail = task;
    const release = () => { if (tail === task) tail = null; };
    task.then(release, release);
    return task;
  };
  const cartUrl = () => `${window.Shopify?.routes?.root || '/'}cart`;
  const target = () => document.querySelector('cart-drawer') || document.querySelector('cart-items') || document;

  const refresh = async () => {
    try {
      const roots = [...document.querySelectorAll('cart-drawer, cart-items')];
      const sectionIds = roots.map((root) => root.matches('cart-drawer') ? 'cart-drawer' : root.dataset.sectionId).filter(Boolean);
      const url = new URL(window.location.href);
      url.searchParams.set('sections', [...new Set(sectionIds)].join(','));
      const cartResponse = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Cart read failed');
      const cart = await cartResponse.json();
      if (typeof cart.item_count !== 'number') throw new Error('Invalid cart response');
      if (sectionIds.length) {
        const sectionResponse = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        if (!sectionResponse.ok) throw new Error('Cart sections failed');
        const sections = await sectionResponse.json();
        for (const root of roots) {
          const id = root.matches('cart-drawer') ? 'cart-drawer' : root.dataset.sectionId;
          if (!root.refresh(sections[id])) throw new Error('Missing cart section');
        }
      }
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });
      return cart;
    } catch {
      window.location.href = cartUrl();
      return null;
    }
  };

  // Standard actions emit their own events. Only our Ajax handlers call begin.
  const begin = (root, eventClass, payload) => {
    const events = window.StandardEvents;
    const EventClass = events?.[eventClass];
    if (!EventClass || !root.dispatchEvent) return { resolve() {}, reject() {} };
    const deferred = EventClass.createPromise();
    deferred.promise.catch(() => {});
    root.dispatchEvent(new EventClass({ ...payload, promise: deferred.promise }));
    return {
      resolve(cart, userErrors) {
        let converted = null;
        try { if (cart) converted = EventClass.createCartFromAjaxResponse(cart); } catch { /* Cart read unavailable. */ }
        deferred.resolve({ cart: converted, ...(userErrors ? { userErrors } : {}) });
      },
      reject(error) {
        const failure = error instanceof Error ? error : new Error('Cart request failed');
        deferred.reject(failure);
        if (events.CartErrorEvent) root.dispatchEvent(new events.CartErrorEvent({ error: failure.message, code: 'SERVICE_UNAVAILABLE' }));
      },
    };
  };

  const configureActions = () => {
    const actions = window.Shopify?.actions;
    actions?.updateCart?.configure?.({
      eventTarget: target,
      async handler(defaultHandler) {
        return run(async () => {
          try { return await defaultHandler(); }
          finally { await refresh(); }
        });
      },
    });
    actions?.openCart?.configure?.({
      async handler() {
        if (tail) await tail.catch(() => {});
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.open();
        else window.location.href = cartUrl();
      },
    });
  };

  window.DrFussCart = { run, begin, configureActions };
})();
